<script setup lang="ts">
import { listDiveCenters } from '@/api/dive-centers'
import { ApiError } from '@/api/client'
import { facilityIcon, labelForOffering } from '@/data/offerings'
// Estado de publicación del DC. La verificación es a nivel cuenta y se muestra
// en el navbar, no acá.
import { statusChip } from '@/data/diveCenterStatus'
import { useAuthStore } from '@/stores/auth'
import { initialsOf } from '@/utils/initials'
import type { DiveCenter } from '@/types'

definePage({ meta: { requiresAuth: true } })

/**
 * Cuántos íconos de facilities entran antes del "+".
 *
 * Cinco y el sexto lugar para el resto: con más, el grupo de avatares empuja al
 * contador de servicios y la fila se parte en dos en las columnas angostas.
 */
const FACILITIES_SHOWN = 5

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()

const loading = ref(true)
const error = ref('')
const diveCenters = ref<DiveCenter[]>([])

const reachedLimit = computed(() => !!auth.provider && diveCenters.value.length >= auth.provider.maxDiveCenters)

/*
 * El rating de Google salió de la tarjeta junto con el resto del cuerpo: es una
 * request en vivo por centro (no se puede persistir) y una fila que aparece en
 * unos centros y en otros no, justo lo que rompía el alto parejo. Sigue estando
 * en el Overview del centro, a un click.
 */

/** Los primeros íconos de facilities y cuántos quedaron afuera. */
const facilitiesOf = (dc: DiveCenter) => {
  const all = dc.facilities ?? []

  return { shown: all.slice(0, FACILITIES_SHOWN), rest: Math.max(0, all.length - FACILITIES_SHOWN) }
}

const servicesLabel = (dc: DiveCenter) => {
  const n = dc.services?.length ?? 0

  return n === 1 ? '1 service' : `${n} services`
}

// Solo el owner crea dive centers; admins y managers gestionan los existentes.
const isOwner = computed(() => auth.providerUser?.role === 'owner')

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await listDiveCenters()

    diveCenters.value = res.diveCenters

    // Con un solo centro esta pantalla sería una tarjeta suelta y el cartel del
    // límite del plan: un paso vacío antes de lo que el usuario vino a hacer.
    // Se entra directo al centro. `replace` para que el botón "atrás" del
    // navegador no rebote entre la lista y el centro.
    // Escape: `?keep=1`, para poder ver la lista a propósito aunque haya uno
    // solo.
    if (diveCenters.value.length === 1 && route.query.keep !== '1') {
      await router.replace({
        name: 'dive-centers-id',
        params: { id: diveCenters.value[0].id },
      })

      return
    }
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load dive centers.'
  }
  finally {
    loading.value = false
  }
}

onMounted(async () => {
  await auth.refreshMe()
  await load()
})

const goNew = () => {
  if (reachedLimit.value || !isOwner.value)
    return

  router.push({ name: 'dive-centers-new' })
}
</script>

<template>
  <div>
    <!-- Sin título de página (lo dice el menú): queda la acción sola, alineada
         a la derecha como estaba dentro del encabezado. -->
    <div class="d-flex justify-end mb-6">
      <VBtn
        v-if="isOwner"
        prepend-icon="ri-add-line"
        :disabled="reachedLimit"
        :title="reachedLimit ? `Plan limit: ${auth.provider?.maxDiveCenters} dive center(s)` : ''"
        @click="goNew"
      >
        New dive center
      </VBtn>
    </div>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-4"
    >
      {{ error }}
    </VAlert>

    <div
      v-if="loading"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <VCard
      v-else-if="!diveCenters.length"
      class="text-center pa-12"
    >
      <VIcon
        icon="ri-store-2-line"
        size="48"
        color="primary"
        class="mb-4"
      />
      <h6 class="text-h6 mb-2">
        No dive centers yet
      </h6>
      <p class="text-medium-emphasis mb-6">
        {{ isOwner
          ? 'Create your first dive center and start showing up to divers in the app.'
          : 'No dive centers yet. The account owner can add them.' }}
      </p>
      <VBtn
        v-if="isOwner"
        :disabled="reachedLimit"
        @click="goNew"
      >
        Create your first dive center
      </VBtn>
    </VCard>

    <!-- 3 por fila desde md; la card entera linkea al hub del centro
         (editar/borrar viven adentro, no acá). -->
    <VRow v-else>
      <VCol
        v-for="dc in diveCenters"
        :key="dc.id"
        cols="12"
        sm="6"
        md="4"
      >
        <!--
          La tarjeta es la `CardBasic` de la plantilla (el bloque "Robert
          Meyer"): portada, avatar montado sobre ella, nombre + ubicación a la
          izquierda con la acción a la derecha, y un pie con un dato y su grupo
          de avatares.

          Todo lo que variaba de alto —descripción, estrellas de Google, chips
          de servicios— salió: con las filas fijas, las tarjetas miden lo mismo
          sin depender de cuántos datos cargó cada centro. El nombre y la ciudad
          se recortan a una línea.
        -->
        <VCard
          :to="{ name: 'dive-centers-id', params: { id: dc.id } }"
          class="h-100"
        >
          <VImg
            :src="dc.photos[0] || undefined"
            height="140"
            cover
          >
            <template #placeholder>
              <div class="d-flex align-center justify-center fill-height bg-grey-100">
                <VIcon
                  icon="ri-image-line"
                  size="36"
                  class="text-disabled"
                />
              </div>
            </template>

            <!-- El estado no entra en el cuerpo sin desalinear las filas, así
                 que va sobre la portada. -->
            <div class="d-flex justify-end pa-2">
              <!-- `flat` y no el tonal de siempre: sobre una foto el fondo
                   translúcido desaparece y queda el texto suelto. -->
              <VChip
                :color="statusChip(dc.status).color"
                variant="flat"
                size="small"
                label
              >
                {{ statusChip(dc.status).label }}
              </VChip>
            </div>
          </VImg>

          <VCardText class="position-relative">
            <!-- El logo del centro, o sus iniciales — igual que en el selector
                 de la cabecera. -->
            <VAvatar
              size="75"
              class="avatar-center"
              :color="dc.logoUrl ? undefined : 'primary'"
              :variant="dc.logoUrl ? undefined : 'tonal'"
            >
              <VImg
                v-if="dc.logoUrl"
                :src="dc.logoUrl"
              />
              <span
                v-else
                class="text-h5 font-weight-medium"
              >{{ initialsOf(dc.name) }}</span>
            </VAvatar>

            <div class="d-flex justify-space-between align-center flex-nowrap ga-2 pt-8">
              <div class="me-2 overflow-hidden">
                <VCardTitle class="pa-0 text-truncate">
                  {{ dc.name }}
                </VCardTitle>
                <VCardSubtitle class="text-caption pa-0 text-truncate">
                  {{ dc.address?.city || dc.address?.formattedAddress || 'Address not set' }}
                </VCardSubtitle>
              </div>

              <VBtn
                size="small"
                :to="{ name: 'dive-centers-id', params: { id: dc.id } }"
              >
                Manage
              </VBtn>
            </div>

            <div class="d-flex justify-space-between align-center flex-nowrap ga-2 mt-8">
              <span class="font-weight-medium text-no-wrap">{{ servicesLabel(dc) }}</span>

              <!-- Facilities en el lugar de los avatares del diseño. -->
              <div class="v-avatar-group">
                <VAvatar
                  v-for="code in facilitiesOf(dc).shown"
                  :key="`f-${code}`"
                  size="40"
                  color="primary"
                  variant="tonal"
                >
                  <VTooltip
                    activator="parent"
                    location="top"
                    :text="labelForOffering(code)"
                  />
                  <VIcon
                    :icon="facilityIcon(code)"
                    size="20"
                  />
                </VAvatar>

                <!-- El sexto lugar es el resto, no una facility más. -->
                <VAvatar
                  v-if="facilitiesOf(dc).rest"
                  size="40"
                  color="secondary"
                  variant="tonal"
                >
                  <VTooltip
                    activator="parent"
                    location="top"
                    :text="`${facilitiesOf(dc).rest} more`"
                  />
                  <VIcon
                    icon="ri-add-line"
                    size="20"
                  />
                </VAvatar>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <p
      v-if="!loading && reachedLimit"
      class="text-medium-emphasis text-body-2 mt-4"
    >
      You've reached your plan limit of {{ auth.provider?.maxDiveCenters }} dive center(s).
    </p>
  </div>
</template>

<style lang="scss" scoped>
/* Tal cual la trae `CardBasic` de la plantilla: el avatar monta sobre la
   portada y el borde del color de la superficie lo recorta contra la foto. */
.avatar-center {
  position: absolute;
  border: 3px solid rgb(var(--v-theme-surface));
  inset-block-start: -2rem;
  inset-inline-start: 1rem;
}
</style>
